// ─────────────────────────────────────────────────────────────
//  ARORA TRAVEL ONLINE — SITE CONTENT & CONTACT SETTINGS
//  This is the ONLY file you need to edit to update contact
//  details, WhatsApp number, destinations and services text.
// ─────────────────────────────────────────────────────────────
import {
  Plane, Map, Globe2, SlidersHorizontal, Ship, ShieldCheck,
  Stamp, Briefcase,
} from "lucide-react";

export const SITE = {
  name: "ARORA TRAVEL ONLINE",
  shortName: "ATO",
  tagline: "Your Next Adventure Starts Here",
  city: "Rohini, New Delhi, India",

  // ── BUSINESS CONTACT DETAILS ──────────────────────────────
  // WhatsApp business number: country code + number, DIGITS ONLY
  whatsappNumber: "919220822256",
  whatsappDisplay: "+91 92208 22256",
  phoneDisplay: "+91 92208 22256",
  email: "bookings@aroratravelonline.com",
  address: "SF-10, SWASDEE 18, Block-E, Sector 18, Rohini, Delhi-110089",

  socials: [
    { label: "Instagram", handle: "@arora_travel_online", url: "https://www.instagram.com/arora_travel_online/" },
  ],
};

export const waLink = (message) =>
  `https://wa.me/${SITE.whatsappNumber}?text=${encodeURIComponent(message)}`;

export const WA_DEFAULT_MSG =
  "Hi ATO, I would like to enquire about your travel services.";

export const IMAGES = {
  hero: "https://images.pexels.com/photos/11434413/pexels-photo-11434413.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  about: "https://images.pexels.com/photos/5044866/pexels-photo-5044866.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  flightsVisa: "https://images.unsplash.com/photo-1655722724447-2d2a3071e7f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHw0fHxmbGlnaHQlMjBwYXNzcG9ydCUyMGNydWlzZSUyMHRyYXZlbCUyMHZpc2ElMjBpbnN1cmFuY2UlMjBjb3Jwb3JhdGUlMjB0cmF2ZWx8ZW58MHx8fHwxNzkyODY5MDU2fDA&ixlib=rb-4.1.0&q=85",
};

export const SERVICES = [
  { slug: "flight-bookings", title: "Flight Bookings", icon: Plane,
    description: "Assistance with domestic and international flight options, matched to your dates and budget." },
  { slug: "domestic-holiday-packages", title: "Domestic Holiday Packages", icon: Map,
    description: "Discover destinations and holidays across India — mountains, beaches, heritage and more." },
  { slug: "international-holiday-packages", title: "International Holiday Packages", icon: Globe2,
    description: "Explore international destinations and holiday options, planned around the way you travel." },
  { slug: "custom-holiday-packages", title: "Customized Travel Plans", icon: SlidersHorizontal,
    description: "Travel plans tailored to your destination, preferences and budget — built just for you." },
  { slug: "cruise-bookings", title: "Cruise Bookings", icon: Ship,
    description: "Explore cruise holidays, itineraries and available cruise options with our team's guidance." },
  { slug: "travel-insurance-assistance", title: "Travel Insurance Assistance", icon: ShieldCheck,
    description: "Guidance to help you explore suitable travel insurance options for your trip." },
  { slug: "visa-assistance", title: "Visa Assistance", icon: Stamp,
    description: "Guidance on visa requirements, documentation and the application process." },
  { slug: "corporate-travel-solutions", title: "Corporate Travel Solutions", icon: Briefcase,
    description: "Travel planning and booking assistance for businesses and corporate travellers." },
];

export const DESTINATIONS = [
  { name: "Seychelles", country: "Indian Ocean", category: "International",
    image: "https://images.pexels.com/photos/5044865/pexels-photo-5044865.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    description: "Granite islands, powder-white beaches and turquoise lagoons made for slow, sunlit days." },
  { name: "Dubai", country: "United Arab Emirates", category: "International",
    image: "https://images.unsplash.com/flagged/photo-1559717201-fbb671ff56b7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHszfHxkdWJhaSUyMHNreWxpbmUlMjBidXJqJTIwa2hhbGlmYSUyMGx1eHVyeXxlbnwwfHx8fDE3OTI4NjkwNjJ8MA&ixlib=rb-4.1.0&q=85",
    description: "Futuristic skylines, desert safaris, world-class shopping and waterfront dining." },
  { name: "Maldives", country: "Indian Ocean", category: "International",
    image: "https://images.pexels.com/photos/28408440/pexels-photo-28408440.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    description: "Overwater villas, coral reefs and crystal-clear lagoons — the classic island escape." },
  { name: "Switzerland", country: "Europe", category: "International",
    image: "https://images.pexels.com/photos/33801167/pexels-photo-33801167.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    description: "Alpine peaks, scenic rail journeys, still lakes and storybook villages." },
  { name: "Bali", country: "Indonesia", category: "International",
    image: "https://images.unsplash.com/photo-1746138706445-86f4ee32a676?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NxA1NDR8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjB0cmF2ZWwlMjBmYW1pbHklMjByb21hbnRpYyUyMGJlYWNoJTIwYWR2ZW50dXJlJTIwdHJvcGljYWwlMjBpc2xhbmR8ZW58MHx8fHwxNzkyODY5MDU2fDA&ixlib=rb-4.1.0&q=85",
    description: "Temples, emerald rice terraces, surf beaches and a vibrant island culture." },
  { name: "Kerala", country: "India", category: "Domestic",
    image: "https://images.pexels.com/photos/12066477/pexels-photo-12066477.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    description: "Backwaters, tea gardens, Ayurveda and serene beaches in God's Own Country." },
];

export const TRAVEL_STYLES = [
  { title: "Family Holidays", description: "Easy-paced trips with something for every generation.",
    image: "https://images.pexels.com/photos/12720663/pexels-photo-12720663.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { title: "Romantic Getaways", description: "Quiet escapes, sunsets and stays made for two.",
    image: "https://images.unsplash.com/photo-1761065002557-d9cc398e5f03?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NxA1NDR8MHwxfHNlYXJjaHszfHxsdXh1cnklMjB0cmF2ZWwlMjBmYW1pbHklMjByb21hbnRpYyUyMGJlYWNoJTIwYWR2ZW50dXJlJTIwdHJvcGljYWwlMjBpc2xhbmR8ZW58MHx8fHwxNzkyODY5MDU2fDA&ixlib=rb-4.1.0&q=85" },
  { title: "Beach Escapes", description: "Powdery sands, warm water and unhurried days.",
    image: "https://images.pexels.com/photos/11434425/pexels-photo-11434425.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { title: "Adventure Trips", description: "Trails, peaks and experiences that raise your pulse.",
    image: "https://images.unsplash.com/photo-1547131303-543b40443e96?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHxfHxzd2l0emVybGFuZCUyMGFscHMlMjBtb3VudGFpbnN8ZW58MHx8fHwxNzkyODY5MDYyfDA&ixlib=rb-4.1.0&q=85" },
];

// ── CUSTOMER REVIEWS ─────────────────────────────────────────
// Genuine customer reviews. To add another, copy a block below.
// Set sample: true only for placeholder cards (shows a SAMPLE badge).
export const REVIEWS = [
  { name: "Dilip Bhandari", trip: "Group Travel", rating: 5, sample: false,
    text: "Excellent service and a very smooth travel experience! Professional, helpful, and always available when needed. Highly recommended for hassle-free travel planning. ✈️🌍" },
  { name: "Joney Mutneja", trip: "International Travel — Malaysia", rating: 5, sample: false,
    text: "If you are looking for a planner who takes all the hassle for your travel arrangements then Arora travel is the one you should opt for. They made sure i had all the fun on my trip to malaysia. I just left upon them and it was my best decision. A truly 5 star service." },
  { name: "Amol Pathak", trip: "Group Travel — Phuket and Krabi", rating: 5, sample: false,
    text: "Me and my team of 6 went to Phuket and krabi. Best service I have ever seen, everything is perfectly organised by team. Very good hotels assigned with best travel experience. You people made our vacation memorable. Will soon experience your services again." },
];

export const MARQUEE_ITEMS = [
  "Seychelles", "Dubai", "Maldives", "Switzerland", "Bali", "Kerala",
  "Cruise Holidays", "Family Trips", "Romantic Getaways", "Adventure",
];
